// app/(main)/checkout/page.tsx
"use client";

import { ArrowLeft, Truck, CreditCard, MapPin, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { orderService } from "@cabukaka/services";
import { useAuth, useToken } from "@cabukaka/hooks";
import { Client, Order } from "@cabukaka/types";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../hooks/cart/useCart";
import { PageContainer } from "../../../components/ui/PageContainer";

export default function CheckoutPage() {
  const navigateTo = useNavigate();
  const { items, subtotal, deliveryFee, total, isLoading: cartLoading, clearCart } = useCart();
  const [step, setStep] = useState<"address" | "payment" | "confirmation">("address");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"mobile_money" | "cash" | "bank_transfer">("mobile_money");
  const [orderPlaced, setOrderPlaced] = useState(false);

  const { token } = useToken();
  const { user } = useAuth();

  // Formulaire d'adresse - saisie manuelle
  const [deliveryInfo, setDeliveryInfo] = useState({
    country: "Cameroun",
    city: "",
    address: "",
    phone: ""
  });

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDeliveryInfo(prev => ({ ...prev, [name]: value }));
  };

  const isAddressValid = () => {
    return deliveryInfo.city.trim() !== "" &&
      deliveryInfo.address.trim() !== "" &&
      deliveryInfo.phone.trim() !== "";
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const handlePlaceOrder = async () => {
    if (!isAddressValid()) {
      alert("Veuillez remplir toutes les informations de livraison");
      return;
    }

    setIsPlacingOrder(true);

    if (!token || !user) {
      navigateTo("/auth/login");
      setIsPlacingOrder(false);
      return;
    }

    const clientProfile = user?.profile as Client;

    const orderData: Partial<Order> = {
      client: {
        name: clientProfile?.name || user.username,
        email: user.email,
        phone: deliveryInfo.phone,
        deliveryAddress: {
          address: deliveryInfo.address,
          city: deliveryInfo.city,
          country: deliveryInfo.country
        }
      },
      items: items.map(item => ({
        productId: item.productId,
        shopId: item.shopId,
        quantity: item.quantity,
        price: item.price,
        subtotal: item.price * item.quantity,
        product: {
          id: item.productId,
          name: item.name,
          price: item.price,
          mainImage: item.image
        },
        shop: {
          id: item.shopId,
          name: item.shopName,
          logo: item.shopLogo || ""
        },
        status: "pending"
      })),
      notes: "",
      paymentMethod: paymentMethod,
      paymentStatus: "pending",
      status: "pending"
    };

    console.log(orderData)

    try {
      await orderService.baseService.create(orderData, token);
      setOrderPlaced(true);
      clearCart();
      setStep("confirmation");
    } catch (error) {
      console.error(error);
      alert("Une erreur est survenue lors de la commande");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (cartLoading) {
    return (
      <PageContainer>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  if (items.length === 0) {
    return (
      <PageContainer>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center py-16 px-4">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4 md:mb-6">
            <CreditCard className="w-10 h-10 md:w-12 md:h-12 text-gray-400" />
          </div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
            Votre panier est vide
          </h2>
          <p className="text-sm md:text-base text-gray-500 mb-4 md:mb-6">
            Ajoutez des produits avant de passer commande
          </p>
          <button
            onClick={() => navigateTo("/products")}
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
          >
            Découvrir nos produits
          </button>
        </div>
      </PageContainer>
    );
  }

  if (orderPlaced) {
    return (
      <PageContainer>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center py-16 px-4">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-green-100 rounded-full flex items-center justify-center mb-4 md:mb-6">
            <Check className="w-10 h-10 md:w-12 md:h-12 text-green-600" />
          </div>
          <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
            Commande confirmée !
          </h2>
          <p className="text-sm md:text-base text-gray-500 mb-6">
            Merci pour votre commande. Vous recevrez un email de confirmation.
          </p>
          <button
            onClick={() => navigateTo("/compte/mes-commandes")}
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
          >
            Voir mes commandes
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="py-4 md:py-6 lg:py-8 px-3 md:px-0">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-6">
          <button
            onClick={() => navigateTo("/panier")}
            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
            Paiement
          </h1>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 md:mb-8 px-2 md:px-4 max-w-md mx-auto">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm ${step === "address" ? "bg-primary text-white" : "bg-green-500 text-white"}`}>
              {step !== "address" ? <Check className="w-3 h-3 md:w-5 md:h-5" /> : "1"}
            </div>
            <span className="text-[10px] md:text-sm mt-1 text-gray-600">Adresse</span>
          </div>
          <div className={`flex-1 h-0.5 mx-1 md:mx-2 ${step !== "address" ? "bg-green-500" : "bg-gray-200"}`} />
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm ${step === "payment" ? "bg-primary text-white" : step === "confirmation" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>
              {step === "confirmation" ? <Check className="w-3 h-3 md:w-5 md:h-5" /> : "2"}
            </div>
            <span className="text-[10px] md:text-sm mt-1 text-gray-600">Paiement</span>
          </div>
          <div className={`flex-1 h-0.5 mx-1 md:mx-2 ${step === "confirmation" ? "bg-green-500" : "bg-gray-200"}`} />
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm ${step === "confirmation" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>
              3
            </div>
            <span className="text-[10px] md:text-sm mt-1 text-gray-600">Confirmation</span>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Formulaire */}
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-none">
            {/* Étape 1: Adresse */}
            {step === "address" && (
              <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                  Adresse de livraison
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                    <select
                      name="country"
                      value={deliveryInfo.country}
                      onChange={handleAddressChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="Cameroun">Cameroun</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                    <input
                      type="text"
                      name="city"
                      value={deliveryInfo.city}
                      onChange={handleAddressChange}
                      placeholder="Douala, Yaoundé, Garoua..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
                    <input
                      type="text"
                      name="address"
                      value={deliveryInfo.address}
                      onChange={handleAddressChange}
                      placeholder="Rue, quartier, numéro..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={deliveryInfo.phone}
                      onChange={handleAddressChange}
                      placeholder="6 XX XX XX XX"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>

                <button
                  onClick={() => setStep("payment")}
                  disabled={!isAddressValid()}
                  className="w-full mt-4 md:mt-6 bg-primary text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                >
                  Continuer
                </button>
              </div>
            )}

            {/* Étape 2: Paiement */}
            {step === "payment" && (
              <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                  Mode de paiement
                </h2>

                <div className="space-y-3">
                  <label
                    className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-xl cursor-pointer transition-colors ${paymentMethod === "mobile_money"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200"
                      }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="mobile_money"
                      checked={paymentMethod === "mobile_money"}
                      onChange={() => setPaymentMethod("mobile_money")}
                      className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm md:text-base">Mobile Money</p>
                      <p className="text-xs md:text-sm text-gray-500">Orange Money, MTN Mobile Money</p>
                    </div>
                  </label>

                  <label
                    className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-xl cursor-pointer transition-colors ${paymentMethod === "bank_transfer"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200"
                      }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="bank_transfer"
                      checked={paymentMethod === "bank_transfer"}
                      onChange={() => setPaymentMethod("bank_transfer")}
                      className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm md:text-base">Virement bancaire</p>
                      <p className="text-xs md:text-sm text-gray-500">Transfert direct</p>
                    </div>
                  </label>

                  <label
                    className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 border rounded-xl cursor-pointer transition-colors ${paymentMethod === "cash"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200"
                      }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="cash"
                      checked={paymentMethod === "cash"}
                      onChange={() => setPaymentMethod("cash")}
                      className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm md:text-base">Paiement à la livraison</p>
                      <p className="text-xs md:text-sm text-gray-500">Payez à la réception</p>
                    </div>
                  </label>
                </div>

                <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-6">
                  <button
                    onClick={() => setStep("address")}
                    className="order-2 md:order-1 py-2.5 md:py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors text-sm md:text-base"
                  >
                    Retour
                  </button>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={isPlacingOrder}
                    className="order-1 md:order-2 flex-1 bg-primary text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
                  >
                    {isPlacingOrder ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Commande en cours...
                      </>
                    ) : (
                      "Confirmer la commande"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Résumé commande */}
          <div className="lg:col-span-1 order-1 lg:order-none mb-4 lg:mb-0">
            <div className="bg-gray-50 rounded-xl p-4 md:p-6 sticky top-[140px]">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
                Résumé de la commande
              </h2>

              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {items.map((item, index) => (
                  <div key={item.id + index} className="flex gap-2 md:gap-3">
                    <div className="relative w-10 h-10 md:w-12 md:h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-800 truncate">{item.name}</p>
                      <p className="text-[10px] md:text-xs text-gray-500">Qté: {item.quantity}</p>
                    </div>
                    <p className="text-xs md:text-sm font-semibold text-gray-900">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-3 border-t border-gray-200">
                <div className="flex justify-between text-gray-600 text-xs md:text-sm">
                  <span>Sous-total</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600 text-xs md:text-sm">
                  <span>Livraison</span>
                  <span>{formatPrice(deliveryFee)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-bold text-gray-900 text-sm md:text-base">Total</span>
                  <span className="text-base md:text-lg font-bold text-primary">
                    {formatPrice(total)}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs text-gray-500">
                  <Truck className="w-3 h-3" />
                  <span>Livraison estimée: 2-3 jours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}